import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Users, Settings, BarChart3, ArrowLeft, Send, Loader2, RefreshCw, Image, Plus, Eye, EyeOff, Star, Trash2, Briefcase, TrendingUp, Network, Mail, CheckCircle, XCircle, Receipt, Download, ChevronLeft, ChevronRight, UserCog, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api, { formatZARFull, LOCATIONS, FINISH_LEVELS, PROJECT_TYPES } from '@/lib/api';
import { toast } from 'sonner';
import StudioWorkspace from '@/components/StudioWorkspace';
import PracticeIntelligence from '@/components/PracticeIntelligence';
import ProfessionalNetwork from '@/components/ProfessionalNetwork';
import StudioAdminPanel from '@/components/StudioAdminPanel';
import ProposalsStudio from '@/components/ProposalsStudio';
import PortfolioEditor from '@/components/PortfolioEditor';
import AboutEditor from '@/components/AboutEditor';
import LeadArtifacts from '@/components/LeadArtifacts';
import PasswordChangeGate from '@/components/PasswordChangeGate';

const STATUS_COLORS = {
  new: 'bg-gray-100 text-gray-700 border-gray-300',
  qualified: 'bg-green-100 text-green-700 border-green-300',
  follow_up: 'bg-amber-100 text-amber-700 border-amber-300',
  contacted: 'bg-blue-100 text-blue-700 border-blue-300',
  converted: 'bg-brand-accent/20 text-brand-accent-dark border-brand-accent/50',
  lost: 'bg-red-100 text-red-700 border-red-300',
};

export default function AdminDashboard() {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [proposalPrefill, setProposalPrefill] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeProjects, setActiveProjects] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertForm, setConvertForm] = useState({ lead_id: '', project_name: '', total_fee: '' });
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailTesting, setEmailTesting] = useState(false);
  const [leadSources, setLeadSources] = useState([]);
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [newLead, setNewLead] = useState({ name: '', email: '', phone: '', source: '', source_note: '', lead_type: 'new_build', notes: '' });
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [manualProject, setManualProject] = useState({ project_name: '', client_name: '', client_email: '', location: '', project_type: 'new_build', source: '', total_fee: '', initial_assumptions: '' });
  const [billingProfile, setBillingProfile] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [whatsappStats, setWhatsappStats] = useState(null);
  const [toolsFunnel, setToolsFunnel] = useState(null);

  useEffect(() => {
    api.get('/leads/sources').then(r => setLeadSources(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === 'leads') fetchLeads();
    if (tab === 'analytics') fetchAnalytics();
    if (tab === 'settings') { fetchConfig(); fetchEmailConfig(); fetchBillingProfile(); }
    if (tab === 'projects') fetchActiveProjects();
    if (tab === 'invoices') fetchAllInvoices();
  }, [tab]);

  const fetchLeads = async () => {
    try { const { data } = await api.get('/leads'); setLeads(data); } catch { toast.error('Failed to load leads'); }
    // Load WhatsApp funnel stats alongside — silent failure is fine, the card
    // just won't render.
    api.get('/studio/whatsapp/stats').then(r => setWhatsappStats(r.data)).catch(() => setWhatsappStats(null));
    // Per-tool funnel (impressions → leads → PDF downloads). Same silent-fail
    // pattern — dashboard degrades gracefully if the endpoint is unreachable.
    api.get('/studio/tools/funnel').then(r => setToolsFunnel(r.data)).catch(() => setToolsFunnel(null));
  };
  const fetchAnalytics = async () => {
    try {
      const { data } = await api.get('/analytics/detailed');
      setAnalytics(data);
    } catch {
      // Graceful fallback — show empty state instead of error
      setAnalytics({
        total_leads: 0, new_leads: 0, qualified_leads: 0, conversion_rate: 0,
        leads_by_source: [], leads_by_status: [],
        tool_usage: [
          { tool: 'Feasibility', count: 0 },
          { tool: 'Cost Calculator', count: 0 },
          { tool: 'Brief Builder', count: 0 },
        ],
        last_30_days: { leads: 0, reports: 0 },
      });
    }
  };
  const fetchConfig = async () => {
    try { const { data } = await api.get('/config'); setConfig(data); } catch { toast.error('Failed to load config'); }
  };
  const fetchActiveProjects = async () => {
    try { const { data } = await api.get('/studio/projects'); setActiveProjects(data); } catch { setActiveProjects([]); }
  };
  const fetchEmailConfig = async () => {
    try { const { data } = await api.get('/email/config'); setEmailConfig(data); } catch { setEmailConfig(null); }
  };
  const sendTestEmail = async () => {
    setEmailTesting(true);
    try {
      await api.post('/email/test', { to: emailConfig?.admin_alert_email });
      toast.success('Test email sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
    finally { setEmailTesting(false); }
  };

  const createManualLead = async () => {
    if (!newLead.source) { toast.error('Lead source is required'); return; }
    setLoading(true);
    try {
      await api.post('/studio/leads', newLead);
      toast.success('Lead created');
      setShowCreateLead(false);
      setNewLead({ name: '', email: '', phone: '', source: '', source_note: '', lead_type: 'new_build', notes: '' });
      fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create lead'); }
    finally { setLoading(false); }
  };

  const createManualProject = async () => {
    if (!manualProject.project_name) { toast.error('Project name required'); return; }
    if (!manualProject.source) { toast.error('Project source required'); return; }
    setLoading(true);
    try {
      await api.post('/studio/projects/manual', manualProject);
      toast.success('Project created');
      setShowCreateProject(false);
      setManualProject({ project_name: '', client_name: '', client_email: '', location: '', project_type: 'new_build', source: '', total_fee: '', initial_assumptions: '' });
      fetchActiveProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create project'); }
    finally { setLoading(false); }
  };

  const deleteDemoData = async () => {
    if (!window.confirm('Remove all demo projects and BIM snapshots? This cannot be undone.')) return;
    try {
      const { data } = await api.delete('/studio/demo-data');
      toast.success(data.message);
      fetchActiveProjects();
    } catch { toast.error('Failed to remove demo data'); }
  };

  const fetchBillingProfile = async () => {
    try { const { data } = await api.get('/studio/billing-profile'); setBillingProfile(data); } catch { setBillingProfile(null); }
  };
  const saveBillingProfile = async () => {
    setLoading(true);
    try {
      await api.put('/studio/billing-profile', billingProfile);
      toast.success('Billing profile saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setLoading(false); }
  };
  const fetchAllInvoices = async () => {
    try { const { data } = await api.get('/studio/invoices'); setAllInvoices(data); } catch { setAllInvoices([]); }
  };
  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/studio/invoices/${invoiceId}/pdf`, { credentials: 'include' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${invoiceNumber || 'invoice'}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const convertLead = async () => {
    if (!convertForm.project_name || !convertForm.lead_id) { toast.error('Please enter a project name'); return; }
    setLoading(true);
    try {
      await api.post('/studio/projects', { ...convertForm, total_fee: Number(convertForm.total_fee) || 0 });
      toast.success('Lead converted to active project');
      setShowConvertDialog(false);
      setConvertForm({ lead_id: '', project_name: '', total_fee: '' });
      fetchLeads();
      setTab('projects');
      fetchActiveProjects();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to convert'); }
    finally { setLoading(false); }
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/leads/${leadId}`, { status });
      toast.success('Status updated');
      fetchLeads();
      if (selectedLead?.lead_id === leadId) setSelectedLead(p => ({ ...p, status }));
    } catch { toast.error('Failed to update'); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedLead) return;
    try {
      await api.post(`/leads/${selectedLead.lead_id}/notes`, { content: noteText });
      toast.success('Note added');
      setNoteText('');
      const { data } = await api.get(`/leads/${selectedLead.lead_id}`);
      setSelectedLead(data);
    } catch { toast.error('Failed to add note'); }
  };

  const deleteLead = async (leadId, leadName) => {
    // Super-admin-only destructive action — one confirm, one toast, refetch.
    const label = leadName ? `"${leadName}"` : 'this lead';
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success('Lead deleted');
      if (selectedLead?.lead_id === leadId) setSelectedLead(null);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete lead');
    }
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      await api.put('/config', config);
      toast.success('Configuration saved');
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const isAdmin = user?.role === 'admin';
  // Restricted users (architects) can view leads + artifacts but cannot
  // touch Settings, Portfolio editing, Analytics, or Invoices. We hide the
  // sidebar entries; the backend role checks still enforce on the API side.
  const navItems = [
    { id: 'leads',     label: 'Leads',     icon: Users },
    { id: 'proposals', label: 'Proposals', icon: FileText },
    { id: 'projects',  label: 'Projects',  icon: Briefcase },
    { id: 'practice',  label: 'Practice',  icon: TrendingUp },
    { id: 'network',   label: 'Network',   icon: Network },
    { id: 'portfolio', label: 'Portfolio', icon: Image,     adminOnly: true },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, adminOnly: true },
    { id: 'invoices',  label: 'Invoices',  icon: Receipt,   adminOnly: true },
    { id: 'settings',  label: 'Settings',  icon: Settings,  adminOnly: true },
  ].filter(item => !item.adminOnly || isAdmin);

  // If a non-admin landed on an admin-only tab (e.g. deep link), bounce to leads.
  useEffect(() => {
    if (!isAdmin && ['portfolio', 'analytics', 'invoices', 'settings'].includes(tab)) {
      setTab('leads');
    }
  }, [isAdmin, tab]);

  return (
    <div className="studio-scope min-h-screen bg-brand-light pt-16 flex" data-testid="admin-dashboard">
      {/* First-login enforcement: block the entire Studio until the user
          rotates their temporary password. */}
      {user?.force_password_change && (
        <PasswordChangeGate
          email={user.email}
          onSuccess={() => checkAuth()}
        />
      )}      {/* Left Sidebar */}
      <aside className={`fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white border-r border-black/10 z-30 transition-all duration-200 ${sidebarCollapsed ? 'w-16' : 'w-52'}`} data-testid="studio-sidebar">
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className={`p-4 border-b border-black/5 ${sidebarCollapsed ? 'px-3' : ''}`}>
            {!sidebarCollapsed && (
              <div className="mb-1">
                <h2 className="font-heading text-sm font-medium text-brand-dark">Raum Studio</h2>
                <p className="text-brand-dark/40 text-[10px] font-body">{user?.name || user?.email}</p>
              </div>
            )}
            {sidebarCollapsed && <div className="w-10 h-10 rounded-sm bg-brand-dark flex items-center justify-center"><span className="text-white font-heading text-xs font-bold">RS</span></div>}
          </div>

          {/* Nav Items */}
          <nav className="flex-1 py-2 overflow-y-auto">
            {navItems.map(item => {
              const Icon = item.icon;
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  data-testid={`tab-${item.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${sidebarCollapsed ? 'justify-center px-3' : ''} ${active ? 'bg-brand-light border-r-2 border-brand-accent text-brand-dark font-medium' : 'text-brand-dark/50 hover:text-brand-dark hover:bg-brand-light/50'}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="text-xs font-body uppercase tracking-wider">{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Collapse Toggle */}
          <div className="p-3 border-t border-black/5">
            <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-full flex items-center justify-center p-2 rounded-sm hover:bg-brand-light text-brand-dark/30 hover:text-brand-dark/60 transition-colors" data-testid="sidebar-toggle">
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
            {!sidebarCollapsed && (
              <div className="mt-2 flex items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] font-body uppercase tracking-wider">{user?.role}</Badge>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-200 ${sidebarCollapsed ? 'ml-16' : 'ml-52'}`}>
        <div className="max-w-7xl mx-auto px-6 py-8">

        {/* LEADS */}
        {tab === 'leads' && (
          selectedLead ? (
              <div data-testid="lead-detail">
                <Button variant="ghost" onClick={() => setSelectedLead(null)} className="mb-4 text-sm" data-testid="lead-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Leads
                </Button>
                <div className="bg-white rounded-sm border border-black/10 p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl font-medium" data-testid="lead-detail-name">{selectedLead.name || 'Unnamed Lead'}</h2>
                      <p className="text-brand-dark/50 font-body text-sm">{selectedLead.email}</p>
                      {selectedLead.phone && <p className="text-brand-dark/40 font-body text-xs mt-1">{selectedLead.phone}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={selectedLead.status} onValueChange={(v) => updateLeadStatus(selectedLead.lead_id, v)}>
                        <SelectTrigger data-testid="lead-status-select" className="w-40 rounded-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {selectedLead.status !== 'converted' && (
                        <Button size="sm" onClick={() => { setConvertForm({ lead_id: selectedLead.lead_id, project_name: `${selectedLead.name || 'Project'} - ${selectedLead.source?.replace('_', ' ')}`, total_fee: '' }); setShowConvertDialog(true); }}
                          data-testid="convert-lead-btn" className="bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm font-body text-xs uppercase tracking-wider whitespace-nowrap">
                          <Briefcase className="w-3.5 h-3.5 mr-1" /> Convert
                        </Button>
                      )}
                      {!['converted', 'lost'].includes(selectedLead.status) && (
                        <Button size="sm" variant="outline" onClick={() => {
                          setProposalPrefill({
                            lead_id: selectedLead.lead_id,
                            client_name: selectedLead.name || selectedLead.email || '',
                            project_name: `${selectedLead.name || 'New'} — ${selectedLead.source?.replace('_', ' ') || 'project'}`,
                            building_type: selectedLead.building_type || 'house',
                            project_type: selectedLead.data?.project_type || 'new_build',
                            cost_of_works: selectedLead.data?.budget || '',
                          });
                          setTab('proposals');
                        }}
                          data-testid="lead-create-proposal-btn"
                          className="rounded-sm font-body text-xs uppercase tracking-wider whitespace-nowrap border-brand-dark/20 text-brand-dark hover:bg-brand-dark hover:text-white">
                          <FileText className="w-3.5 h-3.5 mr-1" /> Create proposal
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 bg-brand-light rounded-sm">
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Source</p>
                      <p className="text-sm font-body font-medium capitalize">{selectedLead.source?.replace('_', ' ')}</p>
                    </div>
                    <div className="p-3 bg-brand-light rounded-sm">
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Interactions</p>
                      <p className="text-sm font-body font-medium">{selectedLead.interaction_count}</p>
                    </div>
                    <div className="p-3 bg-brand-light rounded-sm">
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Created</p>
                      <p className="text-sm font-body font-medium">{new Date(selectedLead.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="p-3 bg-brand-light rounded-sm">
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40">Artifacts</p>
                      <p className="text-sm font-body font-medium">{selectedLead.artifacts?.length || 0}</p>
                    </div>
                  </div>

                  {/* Artifacts */}
                  {/* Client Artifacts — read-only preview & download of every report,
                      brief, and fit/readiness output the client generated. */}
                  <div className="mb-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">
                      Client Artifacts
                    </h3>
                    <LeadArtifacts leadId={selectedLead.lead_id} />
                  </div>

                  <Separator className="my-6" />

                  {/* Notes */}
                  <div>
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-3">Notes</h3>
                    <div className="space-y-3 mb-4">
                      {selectedLead.notes?.map((n, i) => (
                        <div key={i} className="p-3 bg-brand-light/50 rounded-sm">
                          <p className="text-sm font-body">{n.content}</p>
                          <p className="text-xs text-brand-dark/30 mt-1">{n.author} - {new Date(n.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                      {(!selectedLead.notes || selectedLead.notes.length === 0) && (
                        <p className="text-brand-dark/30 text-sm font-body">No notes yet.</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className="rounded-sm min-h-[60px]" />
                      <Button data-testid="note-submit" onClick={addNote} className="bg-brand-dark text-white rounded-sm self-end" size="sm">
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div data-testid="leads-list">
                {/* WhatsApp funnel — visible only when there's at least one click */}
                {whatsappStats && whatsappStats.total.impressions > 0 && (
                  <div className="mb-4 bg-white rounded-sm border border-black/10 p-4" data-testid="whatsapp-funnel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#25D366]" />
                        <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/60">WhatsApp Funnel</p>
                      </div>
                      <p className="text-[10px] text-brand-dark/40 font-body">Last 7 / 30 days · All time</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs text-brand-dark/50 font-body">Button Clicks</p>
                        <p className="text-2xl font-heading font-semibold text-brand-dark tabular-nums">
                          {whatsappStats.last_7_days.impressions}
                          <span className="text-brand-dark/40 text-sm font-normal"> / {whatsappStats.last_30_days.impressions} / {whatsappStats.total.impressions}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-brand-dark/50 font-body">Leads Captured</p>
                        <p className="text-2xl font-heading font-semibold text-brand-dark tabular-nums">
                          {whatsappStats.last_7_days.leads}
                          <span className="text-brand-dark/40 text-sm font-normal"> / {whatsappStats.last_30_days.leads} / {whatsappStats.total.leads}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-brand-dark/50 font-body">Conversion Rate</p>
                        <p className="text-2xl font-heading font-semibold text-brand-accent-dark tabular-nums">
                          {whatsappStats.total.conversion_rate}%
                        </p>
                      </div>
                    </div>
                    {whatsappStats.total.impressions > 20 && whatsappStats.total.conversion_rate < 20 && (
                      <p className="text-[11px] text-amber-700 font-body mt-3 pt-3 border-t border-black/5">
                        Low conversion — visitors are clicking but abandoning the form. Consider simplifying the ask or switching to instant-open.
                      </p>
                    )}
                  </div>
                )}

                {/* Per-tool funnel — one row per calculator */}
                {toolsFunnel && toolsFunnel.tools && toolsFunnel.tools.some(t => t.total.impressions > 0) && (
                  <div className="mb-4 bg-white rounded-sm border border-black/10 p-4" data-testid="tools-funnel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-brand-accent" />
                        <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/60">Tool Funnel — Last 30 Days</p>
                      </div>
                      <p className="text-[10px] text-brand-dark/40 font-body">Criteria filled → Leads → PDF downloads</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm font-body">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-brand-dark/40 border-b border-black/5">
                            <th className="text-left py-2 font-heading">Tool</th>
                            <th className="text-right py-2 font-heading">Filled</th>
                            <th className="text-right py-2 font-heading">Leads</th>
                            <th className="text-right py-2 font-heading">Downloads</th>
                            <th className="text-right py-2 font-heading">Lead %</th>
                            <th className="text-right py-2 font-heading">Download %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {toolsFunnel.tools.map(t => (
                            <tr key={t.tool} data-testid={`tools-funnel-row-${t.tool}`} className="border-b border-black/5 last:border-0">
                              <td className="py-2 font-medium">{t.label}</td>
                              <td className="text-right py-2 tabular-nums">{t.last_30_days.impressions}</td>
                              <td className="text-right py-2 tabular-nums">{t.last_30_days.leads}</td>
                              <td className="text-right py-2 tabular-nums">{t.last_30_days.downloads}</td>
                              <td className={`text-right py-2 tabular-nums ${t.last_30_days.impressions > 10 && t.last_30_days.lead_rate < 20 ? 'text-amber-700' : 'text-brand-dark/70'}`}>
                                {t.last_30_days.lead_rate}%
                              </td>
                              <td className="text-right py-2 tabular-nums text-brand-dark/50">
                                {t.last_30_days.download_rate}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-brand-dark/40 font-body mt-2">
                      Amber = high abandonment. Filled = visitors who completed the criteria form and hit Calculate. Leads = visitors who then unlocked their report by sharing details.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-brand-dark/50 font-body">{leads.length} leads</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={fetchLeads} data-testid="refresh-leads"><RefreshCw className="w-4 h-4" /></Button>
                    <Button size="sm" onClick={() => setShowCreateLead(!showCreateLead)} data-testid="create-lead-btn"
                      className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Lead
                    </Button>
                  </div>
                </div>

                {/* Manual Lead Creation Form */}
                {showCreateLead && (
                  <div className="bg-white rounded-sm border border-black/10 p-6 mb-4" data-testid="create-lead-form">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">New Lead</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <Input value={newLead.name} onChange={(e) => setNewLead(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="rounded-sm h-10" data-testid="lead-input-name" />
                      <Input value={newLead.email} onChange={(e) => setNewLead(p => ({ ...p, email: e.target.value }))} placeholder="Email (optional)" type="email" className="rounded-sm h-10" data-testid="lead-input-email" />
                      <Input value={newLead.phone} onChange={(e) => setNewLead(p => ({ ...p, phone: e.target.value }))} placeholder="Phone (optional)" className="rounded-sm h-10" data-testid="lead-input-phone" />
                      <Select value={newLead.source} onValueChange={(v) => setNewLead(p => ({ ...p, source: v }))}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="lead-input-source"><SelectValue placeholder="Source *" /></SelectTrigger>
                        <SelectContent>{leadSources.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={newLead.lead_type} onValueChange={(v) => setNewLead(p => ({ ...p, lead_type: v }))}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="lead-input-type"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new_build">New Build</SelectItem>
                          <SelectItem value="renovation">Renovation</SelectItem>
                          <SelectItem value="advisory">Advisory</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      {newLead.source === 'other' && (
                        <Input value={newLead.source_note} onChange={(e) => setNewLead(p => ({ ...p, source_note: e.target.value }))} placeholder="Source details" className="rounded-sm h-10" />
                      )}
                    </div>
                    <Textarea value={newLead.notes} onChange={(e) => setNewLead(p => ({ ...p, notes: e.target.value }))} placeholder="Initial notes (optional)" className="rounded-sm min-h-[60px] mb-3" data-testid="lead-input-notes" />
                    <div className="flex gap-2">
                      <Button onClick={createManualLead} disabled={loading} className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider" data-testid="lead-submit-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Lead'}
                      </Button>
                      <Button variant="ghost" onClick={() => setShowCreateLead(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                {leads.length === 0 ? (
                  <div className="bg-white rounded-sm border border-black/10 p-12 text-center">
                    <Users className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">No leads yet</h3>
                    <p className="text-brand-dark/40 font-body text-sm max-w-md mx-auto mb-6">
                      Leads are captured automatically when visitors use your tools — Feasibility, Cost Calculator, Brief Builder, or the Guided Consultation. Each interaction creates a lead profile with linked reports.
                    </p>
                    <div className="flex items-center justify-center gap-3 text-xs text-brand-dark/30 font-body">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> New</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> Qualified</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Follow Up</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> Contacted</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-sm border border-black/10 overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-brand-light border-b border-black/5 text-xs font-heading uppercase tracking-wider text-brand-dark/40">
                      <div className="col-span-3">Name</div>
                      <div className="col-span-3">Email</div>
                      <div className="col-span-2">Source</div>
                      <div className="col-span-2">Status</div>
                      <div className={user?.is_super_admin ? "col-span-1" : "col-span-2"}>Last Active</div>
                      {user?.is_super_admin && <div className="col-span-1 text-right">Actions</div>}
                    </div>
                    {leads.map((lead) => (
                      <div
                        key={lead.lead_id}
                        data-testid={`lead-row-${lead.lead_id}`}
                        onClick={() => setSelectedLead(lead)}
                        className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-black/5 last:border-0 hover:bg-brand-light/50 cursor-pointer transition-colors text-sm font-body"
                      >
                        <div className="col-span-3 font-medium truncate">{lead.name || 'Unnamed'}</div>
                        <div className="col-span-3 text-brand-dark/60 truncate">{lead.email}</div>
                        <div className="col-span-2 text-brand-dark/50 capitalize truncate">{lead.source?.replaceAll('_', ' ')}</div>
                        <div className="col-span-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs capitalize border ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>
                            {lead.status?.replaceAll('_', ' ')}
                          </span>
                        </div>
                        <div className={`${user?.is_super_admin ? "col-span-1" : "col-span-2"} text-brand-dark/40 text-xs`}>{new Date(lead.last_activity).toLocaleDateString()}</div>
                        {user?.is_super_admin && (
                          <div className="col-span-1 flex justify-end">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); deleteLead(lead.lead_id, lead.name); }}
                              data-testid={`delete-lead-${lead.lead_id}`}
                              className="text-brand-dark/40 hover:text-red-600 p-1 rounded transition-colors"
                              title="Delete lead (super admin only)"
                              aria-label={`Delete lead ${lead.name || lead.email}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          )}

          {/* CONVERT DIALOG */}
          {showConvertDialog && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" data-testid="convert-dialog">
              <div className="bg-white rounded-sm p-6 w-full max-w-md">
                <h3 className="font-heading text-lg font-medium mb-4">Convert Lead to Active Project</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Project Name *</label>
                    <Input data-testid="convert-name" value={convertForm.project_name} onChange={(e) => setConvertForm(p => ({ ...p, project_name: e.target.value }))} className="rounded-sm h-10" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Total Fee (R)</label>
                    <Input data-testid="convert-fee" type="number" value={convertForm.total_fee} onChange={(e) => setConvertForm(p => ({ ...p, total_fee: e.target.value }))} placeholder="Optional" className="rounded-sm h-10" />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button data-testid="convert-submit" onClick={convertLead} disabled={loading} className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Project'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* PROPOSALS TAB */}
          {tab === 'proposals' && (
            <ProposalsStudio
              prefilledCreate={proposalPrefill}
              onPrefillConsumed={() => setProposalPrefill(null)}
            />
          )}

          {/* PROJECTS TAB */}
          {tab === 'projects' && (
            selectedWorkspace ? (
              <StudioWorkspace projectId={selectedWorkspace} onBack={() => { setSelectedWorkspace(null); fetchActiveProjects(); }} />
            ) : (
              <div data-testid="projects-list">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-brand-dark/50 font-body">{activeProjects.length} active project{activeProjects.length !== 1 ? 's' : ''}</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={fetchActiveProjects} data-testid="refresh-projects-live"><RefreshCw className="w-4 h-4" /></Button>
                    <Button size="sm" onClick={() => setShowCreateProject(!showCreateProject)} data-testid="create-project-btn"
                      className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Create Project
                    </Button>
                  </div>
                </div>

                {/* Manual Project Creation Form */}
                {showCreateProject && (
                  <div className="bg-white rounded-sm border border-black/10 p-6 mb-4" data-testid="create-project-form">
                    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-brand-dark/60 mb-4">New Project</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <Input value={manualProject.project_name} onChange={(e) => setManualProject(p => ({ ...p, project_name: e.target.value }))} placeholder="Project name *" className="rounded-sm h-10" data-testid="proj-input-name" />
                      <Input value={manualProject.client_name} onChange={(e) => setManualProject(p => ({ ...p, client_name: e.target.value }))} placeholder="Client name" className="rounded-sm h-10" data-testid="proj-input-client" />
                      <Input value={manualProject.client_email} onChange={(e) => setManualProject(p => ({ ...p, client_email: e.target.value }))} placeholder="Client email" className="rounded-sm h-10" />
                      <Select value={manualProject.location} onValueChange={(v) => setManualProject(p => ({ ...p, location: v }))}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="proj-input-location"><SelectValue placeholder="Location" /></SelectTrigger>
                        <SelectContent>{LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={manualProject.project_type} onValueChange={(v) => setManualProject(p => ({ ...p, project_type: v }))}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="proj-input-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={manualProject.source} onValueChange={(v) => setManualProject(p => ({ ...p, source: v }))}>
                        <SelectTrigger className="rounded-sm h-10" data-testid="proj-input-source"><SelectValue placeholder="Source *" /></SelectTrigger>
                        <SelectContent>{leadSources.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={manualProject.total_fee} onChange={(e) => setManualProject(p => ({ ...p, total_fee: e.target.value }))} placeholder="Total fee (R)" type="number" className="rounded-sm h-10" data-testid="proj-input-fee" />
                    </div>
                    <Textarea value={manualProject.initial_assumptions} onChange={(e) => setManualProject(p => ({ ...p, initial_assumptions: e.target.value }))} placeholder="Initial assumptions / notes (optional)" className="rounded-sm min-h-[60px] mb-3" data-testid="proj-input-assumptions" />
                    <div className="flex gap-2">
                      <Button onClick={createManualProject} disabled={loading} className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider" data-testid="proj-submit-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Project'}
                      </Button>
                      <Button variant="ghost" onClick={() => setShowCreateProject(false)}>Cancel</Button>
                    </div>
                  </div>
                )}
                {activeProjects.length === 0 ? (
                  <div className="bg-white rounded-sm border border-black/10 p-12 text-center">
                    <Briefcase className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">No active projects</h3>
                    <p className="text-brand-dark/40 font-body text-sm max-w-md mx-auto">
                      Create a project directly, or convert a qualified lead from the Leads tab.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeProjects.map((p) => {
                      const completed = p.stages?.filter(s => s.status === 'completed').length || 0;
                      const total = p.stages?.length || 6;
                      const currentStage = p.stages?.find(s => s.status === 'in_progress');
                      return (
                        <div key={p.project_id} onClick={() => setSelectedWorkspace(p.project_id)}
                          className="bg-white rounded-sm border border-black/10 p-4 cursor-pointer hover:border-brand-accent/30 transition-colors"
                          data-testid={`workspace-project-${p.project_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-heading text-sm font-medium">{p.name}</h4>
                              <p className="text-xs text-brand-dark/40 font-body">{p.client_name} — {LOCATIONS.find(l => l.value === p.location)?.label || p.location}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              {p.demo && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">Demo</Badge>}
                              <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                              <span className="text-xs font-body font-medium text-brand-dark/60">{completed}/{total}</span>
                            </div>
                          </div>
                          {currentStage && (
                            <p className="text-xs text-blue-600 font-body mt-1.5">Current: {currentStage.name}</p>
                          )}
                          {/* Progress bar */}
                          <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-accent rounded-full transition-all" style={{ width: `${(completed / total) * 100}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          )}

          {/* PRACTICE TAB */}
          {tab === "practice" && (
            <PracticeIntelligence />
          )}

          {/* NETWORK TAB (C3) */}
          {tab === "network" && (
            <ProfessionalNetwork />
          )}

          {/* PORTFOLIO TAB */}
          {tab === "portfolio" && <PortfolioEditor />}

          {/* ANALYTICS TAB */}
          {tab === "analytics" && (
            analytics ? (
              <div className="space-y-6" data-testid="analytics-view">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Total Leads', value: analytics.total_leads },
                    { label: 'New Leads', value: analytics.new_leads },
                    { label: 'Qualified', value: analytics.qualified_leads },
                    { label: 'Conversion', value: `${analytics.conversion_rate || 0}%` },
                  ].map((s) => (
                    <div key={s.label} className="bg-white rounded-sm border border-black/10 p-5" data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                      <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-1">{s.label}</p>
                      <p className="font-heading text-2xl font-medium text-brand-dark">{s.value}</p>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tool Usage Chart */}
                  <div className="bg-white rounded-sm border border-black/10 p-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Tool Usage</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.tool_usage || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis dataKey="tool" fontSize={11} />
                        <YAxis fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#CBA871" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Leads by Source */}
                  <div className="bg-white rounded-sm border border-black/10 p-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Leads by Source</h3>
                    {analytics.leads_by_source?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={analytics.leads_by_source} cx="50%" cy="50%" outerRadius={70} dataKey="count" nameKey="source"
                            label={({ source, count }) => `${source?.replace('_', ' ')} (${count})`} labelLine={false}>
                            {analytics.leads_by_source.map((_, i) => (
                              <Cell key={i} fill={['#CBA871', '#1A1A1A', '#52525B', '#A1A1AA'][i % 4]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[200px] text-brand-dark/30 text-sm">No lead data yet</div>
                    )}
                  </div>
                </div>

                {/* Leads by Status */}
                <div className="bg-white rounded-sm border border-black/10 p-6">
                  <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Leads by Status</h3>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {(analytics.leads_by_status || []).map((s) => (
                      <div key={s.status} className="p-3 bg-brand-light rounded-sm text-center">
                        <p className="font-heading text-lg font-medium text-brand-dark">{s.count}</p>
                        <p className="text-xs font-body text-brand-dark/50 capitalize">{s.status?.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 30-Day Summary */}
                <div className="bg-white rounded-sm border border-black/10 p-6">
                  <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Last 30 Days</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-brand-light rounded-sm text-center">
                      <p className="font-heading text-xl font-medium text-brand-dark">{analytics.last_30_days?.leads || 0}</p>
                      <p className="text-xs font-body text-brand-dark/50 mt-1">New Leads</p>
                    </div>
                    <div className="p-4 bg-brand-light rounded-sm text-center">
                      <p className="font-heading text-xl font-medium text-brand-dark">{analytics.last_30_days?.reports || 0}</p>
                      <p className="text-xs font-body text-brand-dark/50 mt-1">Reports Generated</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-dark/30" /></div>
            )
          )}

          {/* INVOICES TAB */}
          {tab === "invoices" && (
            <div data-testid="invoices-list">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-brand-dark/50 font-body">{allInvoices.filter(i => i.invoice_number).length} invoices</p>
                <Button variant="ghost" size="sm" onClick={fetchAllInvoices} data-testid="refresh-invoices"><RefreshCw className="w-4 h-4" /></Button>
              </div>
              {allInvoices.filter(i => i.invoice_number).length === 0 ? (
                <div className="bg-white rounded-sm border border-black/10 p-12 text-center">
                  <Receipt className="w-10 h-10 text-brand-dark/15 mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-medium text-brand-dark mb-2">No invoices yet</h3>
                  <p className="text-brand-dark/40 font-body text-sm max-w-md mx-auto">
                    Generate invoices from the Billing tab in any project workspace.
                  </p>
                </div>
              ) : (
                <div className="bg-white rounded-sm border border-black/10 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-black/5">
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Invoice</th>
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Project</th>
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Client</th>
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Total</th>
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-heading uppercase tracking-wider text-brand-dark/40">Date</th>
                      <th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                      {allInvoices.filter(i => i.invoice_number).map(inv => (
                        <tr key={inv.invoice_id} className="border-b border-black/5 hover:bg-brand-light/50">
                          <td className="px-4 py-3 font-body font-medium">{inv.invoice_number}</td>
                          <td className="px-4 py-3 font-body text-brand-dark/60">{inv.project_name}</td>
                          <td className="px-4 py-3 font-body text-brand-dark/60">{inv.client_name}</td>
                          <td className="px-4 py-3 font-body font-medium">R {inv.total?.toLocaleString('en-ZA', {minimumFractionDigits: 2})}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className={`text-xs capitalize ${inv.status === 'draft' ? 'text-gray-500' : inv.status === 'issued' ? 'text-blue-600 border-blue-200' : inv.status === 'paid' ? 'text-green-600 border-green-200' : 'text-red-500 border-red-200'}`}>{inv.status}{inv.waves_issued ? ' (Waves)' : ''}</Badge>
                          </td>
                          <td className="px-4 py-3 font-body text-brand-dark/40 text-xs">{inv.issue_date}</td>
                          <td className="px-4 py-3">
                            <Button size="sm" variant="ghost" onClick={() => downloadInvoicePdf(inv.invoice_id, inv.invoice_number)} data-testid={`dl-${inv.invoice_number}`}>
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {tab === "settings" && (
            config ? (
              <Tabs defaultValue="general" className="w-full" data-testid="settings-tabs">
                <TabsList className="grid grid-cols-5 w-full max-w-2xl bg-brand-light/40 rounded-sm">
                  <TabsTrigger value="general" data-testid="settings-tab-general"
                    className="text-xs font-body uppercase tracking-wider rounded-sm">General</TabsTrigger>
                  <TabsTrigger value="about" data-testid="settings-tab-about"
                    className="text-xs font-body uppercase tracking-wider rounded-sm">About</TabsTrigger>
                  <TabsTrigger value="notifications" data-testid="settings-tab-notifications"
                    className="text-xs font-body uppercase tracking-wider rounded-sm">Notifications</TabsTrigger>
                  <TabsTrigger value="pricing" data-testid="settings-tab-pricing"
                    className="text-xs font-body uppercase tracking-wider rounded-sm">Pricing</TabsTrigger>
                  <TabsTrigger value="system" data-testid="settings-tab-system"
                    className="text-xs font-body uppercase tracking-wider rounded-sm">System</TabsTrigger>
                </TabsList>

                {/* GENERAL — admin/users panel + audit */}
                <TabsContent value="general" className="mt-6 space-y-6" data-testid="settings-panel-general">
                  <StudioAdminPanel user={user} />
                </TabsContent>

                {/* ABOUT — public About page editor */}
                <TabsContent value="about" className="mt-6 space-y-6" data-testid="settings-panel-about">
                  <AboutEditor />
                </TabsContent>

                {/* NOTIFICATIONS — Email / Resend status */}
                <TabsContent value="notifications" className="mt-6 space-y-6" data-testid="settings-panel-notifications">
                  <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="email-settings">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> Email Notifications
                    </h3>
                    {emailConfig ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          {emailConfig.configured ? (
                            <div className="flex items-center gap-2 text-sm font-body text-green-700">
                              <CheckCircle className="w-4 h-4" /> Resend connected
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-sm font-body text-amber-600">
                              <XCircle className="w-4 h-4" /> API key not configured
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm font-body">
                          <div>
                            <p className="text-xs text-brand-dark/40 mb-0.5">Sender</p>
                            <p className="text-brand-dark/70">{emailConfig.sender}</p>
                          </div>
                          <div>
                            <p className="text-xs text-brand-dark/40 mb-0.5">Admin alerts to</p>
                            <p className="text-brand-dark/70">{emailConfig.admin_alert_email || 'Not set'}</p>
                          </div>
                        </div>
                        <div className="text-xs font-body text-brand-dark/40 space-y-1">
                          <p>Report PDF delivery: {emailConfig.configured ? 'Active' : 'Pending key'}</p>
                          <p>Lead alerts: {emailConfig.configured && emailConfig.admin_alert_email ? 'Active' : 'Pending'}</p>
                          <p>Billing alerts: {emailConfig.billing_alerts_enabled ? 'Enabled' : 'Disabled'}</p>
                        </div>
                        {emailConfig.configured && (
                          <Button size="sm" onClick={sendTestEmail} disabled={emailTesting} data-testid="email-test-btn"
                            className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider">
                            {emailTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                            Send Test Email
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-brand-dark/40 font-body">Loading email configuration...</p>
                    )}
                  </div>
                </TabsContent>

                {/* PRICING — cost configs */}
                <TabsContent value="pricing" className="mt-6 space-y-6" data-testid="settings-panel-pricing">
                  {/* Cost Per SQM */}
                  <div className="bg-white rounded-sm border border-black/10 p-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Cost Per m² (ZAR)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(config.cost_per_sqm || {}).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <span className="text-sm font-body font-medium capitalize w-24">{key}</span>
                          <Input
                            data-testid={`config-cost-${key}-min`}
                            type="number" value={val.min}
                            onChange={(e) => setConfig(c => ({ ...c, cost_per_sqm: { ...c.cost_per_sqm, [key]: { ...val, min: Number(e.target.value) } } }))}
                            className="rounded-sm h-9 w-28" placeholder="Min"
                          />
                          <span className="text-brand-dark/30 text-sm">to</span>
                          <Input
                            data-testid={`config-cost-${key}-max`}
                            type="number" value={val.max}
                            onChange={(e) => setConfig(c => ({ ...c, cost_per_sqm: { ...c.cost_per_sqm, [key]: { ...val, max: Number(e.target.value) } } }))}
                            className="rounded-sm h-9 w-28" placeholder="Max"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Location Multipliers */}
                  <div className="bg-white rounded-sm border border-black/10 p-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Location Multipliers</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(config.location_multipliers || {}).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <span className="text-sm font-body w-32 capitalize">{typeof val === 'object' ? val.label : key.replace('_', ' ')}</span>
                          <Input
                            data-testid={`config-loc-${key}`}
                            type="number" step="0.01"
                            value={typeof val === 'object' ? val.multiplier : val}
                            onChange={(e) => setConfig(c => ({
                              ...c, location_multipliers: {
                                ...c.location_multipliers,
                                [key]: typeof val === 'object' ? { ...val, multiplier: Number(e.target.value) } : Number(e.target.value)
                              }
                            }))}
                            className="rounded-sm h-9 w-24"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fees */}
                  <div className="bg-white rounded-sm border border-black/10 p-6">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Professional Fees (%)</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-body w-40">Architecture Fee</span>
                        <Input data-testid="config-arch-min" type="number" value={config.architecture_fee_pct?.min} onChange={(e) => setConfig(c => ({ ...c, architecture_fee_pct: { ...c.architecture_fee_pct, min: Number(e.target.value) } }))} className="rounded-sm h-9 w-20" />
                        <span className="text-brand-dark/30 text-sm">to</span>
                        <Input data-testid="config-arch-max" type="number" value={config.architecture_fee_pct?.max} onChange={(e) => setConfig(c => ({ ...c, architecture_fee_pct: { ...c.architecture_fee_pct, max: Number(e.target.value) } }))} className="rounded-sm h-9 w-20" />
                        <span className="text-brand-dark/30 text-sm">%</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-body w-40">Engineer Fee</span>
                        <Input data-testid="config-eng-min" type="number" value={config.structural_engineer_pct?.min} onChange={(e) => setConfig(c => ({ ...c, structural_engineer_pct: { ...c.structural_engineer_pct, min: Number(e.target.value) } }))} className="rounded-sm h-9 w-20" />
                        <span className="text-brand-dark/30 text-sm">to</span>
                        <Input data-testid="config-eng-max" type="number" value={config.structural_engineer_pct?.max} onChange={(e) => setConfig(c => ({ ...c, structural_engineer_pct: { ...c.structural_engineer_pct, max: Number(e.target.value) } }))} className="rounded-sm h-9 w-20" />
                        <span className="text-brand-dark/30 text-sm">%</span>
                      </div>
                    </div>
                  </div>

                  <Button data-testid="config-save-btn" onClick={saveConfig} disabled={loading} className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-sm font-semibold uppercase tracking-wider">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Pricing Configuration'}
                  </Button>
                </TabsContent>

                {/* SYSTEM — billing profile + demo data */}
                <TabsContent value="system" className="mt-6 space-y-6" data-testid="settings-panel-system">
                  {/* Billing Profile */}
                  <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="billing-profile">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4 flex items-center gap-2">
                      <Receipt className="w-3.5 h-3.5" /> Billing Profile
                    </h3>
                    {billingProfile ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Legal Practice Name</Label>
                            <Input value={billingProfile.legal_name || ''} onChange={e => setBillingProfile(p => ({...p, legal_name: e.target.value}))} className="rounded-sm h-9 text-sm" data-testid="bp-legal-name" />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input value={billingProfile.phone || ''} onChange={e => setBillingProfile(p => ({...p, phone: e.target.value}))} className="rounded-sm h-9 text-sm" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Address</Label>
                            <Textarea value={billingProfile.address || ''} onChange={e => setBillingProfile(p => ({...p, address: e.target.value}))} className="rounded-sm min-h-[60px] text-sm" />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Registration Text</Label>
                            <Textarea value={billingProfile.registration_text || ''} onChange={e => setBillingProfile(p => ({...p, registration_text: e.target.value}))} className="rounded-sm min-h-[40px] text-xs" placeholder="e.g. SACAP Registered Practice" />
                          </div>
                        </div>
                        <div className="pt-2 border-t border-black/5">
                          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-2">VAT</p>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                              <input type="checkbox" checked={billingProfile.vat_registered} onChange={e => setBillingProfile(p => ({...p, vat_registered: e.target.checked}))} className="rounded" />
                              <Label className="text-xs">VAT Registered</Label>
                            </div>
                            <div>
                              <Label className="text-xs">VAT Rate (%)</Label>
                              <Input type="number" value={billingProfile.vat_rate || 15} onChange={e => setBillingProfile(p => ({...p, vat_rate: Number(e.target.value)}))} className="rounded-sm h-9 text-sm w-24" />
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-black/5">
                          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-2">Bank Details</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <Input value={billingProfile.bank_name || ''} onChange={e => setBillingProfile(p => ({...p, bank_name: e.target.value}))} placeholder="Bank name" className="rounded-sm h-9 text-sm" />
                            <Input value={billingProfile.branch_name || ''} onChange={e => setBillingProfile(p => ({...p, branch_name: e.target.value}))} placeholder="Branch" className="rounded-sm h-9 text-sm" />
                            <Input value={billingProfile.branch_code || ''} onChange={e => setBillingProfile(p => ({...p, branch_code: e.target.value}))} placeholder="Branch code" className="rounded-sm h-9 text-sm" />
                            <Input value={billingProfile.account_type || ''} onChange={e => setBillingProfile(p => ({...p, account_type: e.target.value}))} placeholder="Account type" className="rounded-sm h-9 text-sm" />
                            <Input value={billingProfile.account_number || ''} onChange={e => setBillingProfile(p => ({...p, account_number: e.target.value}))} placeholder="Account number" className="rounded-sm h-9 text-sm" />
                          </div>
                        </div>
                        <div className="pt-2 border-t border-black/5">
                          <p className="text-xs font-heading uppercase tracking-wider text-brand-dark/40 mb-2">Invoice Settings</p>
                          <div className="grid grid-cols-3 gap-3">
                            <Input value={billingProfile.invoice_prefix || 'RA-INV'} onChange={e => setBillingProfile(p => ({...p, invoice_prefix: e.target.value}))} placeholder="Prefix" className="rounded-sm h-9 text-sm" data-testid="bp-prefix" />
                            <Input type="number" value={billingProfile.next_invoice_number || 1} onChange={e => setBillingProfile(p => ({...p, next_invoice_number: Number(e.target.value)}))} placeholder="Next #" className="rounded-sm h-9 text-sm" />
                            <Input value={billingProfile.payment_terms || 'Due on receipt'} onChange={e => setBillingProfile(p => ({...p, payment_terms: e.target.value}))} placeholder="Payment terms" className="rounded-sm h-9 text-sm" />
                          </div>
                        </div>
                        <Button onClick={saveBillingProfile} disabled={loading} className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs uppercase tracking-wider" data-testid="bp-save">
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Billing Profile'}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-brand-dark/40 font-body">Loading billing profile...</p>
                    )}
                  </div>

                  {/* Demo Data Management */}
                  <div className="bg-white rounded-sm border border-black/10 p-6" data-testid="demo-data-settings">
                    <h3 className="text-xs font-heading uppercase tracking-wider text-brand-dark/50 mb-4">Demo Data</h3>
                    <p className="text-sm text-brand-dark/50 font-body mb-3">
                      Demo projects and BIM snapshots are available for demonstration purposes. They are clearly flagged and can be removed at any time.
                    </p>
                    <Button size="sm" variant="outline" onClick={deleteDemoData} data-testid="delete-demo-btn"
                      className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm font-body text-xs uppercase tracking-wider">
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove All Demo Data
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-dark/30" /></div>
            )
          )}

        </div>
      </main>
    </div>
  );
}
